import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MapPin, Building2, Briefcase, Clock, Loader2, CheckCircle2, Users } from "lucide-react";
import { Layout } from "@/components/Layout";
import { ShareMenu } from "@/components/ShareMenu";
import { api, useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";

const CUR = { INR: "₹", USD: "$", EUR: "€", GBP: "£", AED: "AED " };

export default function JobDetail() {
  const { id } = useParams();
  const { t } = useLang();
  const { user } = useAuth();
  const nav = useNavigate();
  const [job, setJob] = useState(null);
  const [applied, setApplied] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch(() => {}); }, [id]);

  const apply = async () => {
    if (!user) { nav("/login"); return; }
    if (user.role !== "candidate") { toast.error("Only candidates can apply"); return; }
    setBusy(true);
    try { await api.post("/applications", { job_id: id }); setApplied(true); toast.success("Application sent!"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not apply"); }
    finally { setBusy(false); }
  };

  if (!job) return <Layout><div className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-brand-blue" /></div></Layout>;

  const cur = CUR[job.currency] || "$";
  const shareUrl = `${window.location.origin}/jobs/${id}`;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="gjt-card p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-navy">{job.title}</h1>
              <p className="mt-2 flex items-center gap-2 text-slate-600"><Building2 className="h-4 w-4" /> {job.company_name}</p>
              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-500">
                <span className="flex items-center gap-1"><MapPin className="h-4 w-4" /> {job.city}, {job.country}</span>
                <span className="flex items-center gap-1 capitalize"><Briefcase className="h-4 w-4" /> {job.employment_type}</span>
                <span className="flex items-center gap-1 capitalize"><Clock className="h-4 w-4" /> {job.work_mode}</span>
                <span className="flex items-center gap-1"><Users className="h-4 w-4" /> {job.openings} openings</span>
              </div>
            </div>
            <ShareMenu url={shareUrl} text={`Job: ${job.title} at ${job.company_name}`} label={t("share_job")} testid="share-job" />
          </div>

          <div className="mt-5 inline-flex items-center rounded-xl bg-brand-bluelight px-4 py-2 font-bold text-navy">
            {job.salary_min ? `${cur}${Number(job.salary_min).toLocaleString()} - ${cur}${Number(job.salary_max || job.salary_min).toLocaleString()}` : "Competitive salary"}
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            {(job.skills || []).map((s) => <span key={s} className="rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-600">{s}</span>)}
          </div>

          <div className="mt-6 whitespace-pre-line text-sm leading-relaxed text-slate-700">{job.description}</div>

          <div className="mt-8 flex items-center gap-3">
            {applied ? (
              <span data-testid="applied-badge" className="inline-flex items-center gap-2 rounded-xl bg-emerald-50 px-6 py-3 font-bold text-emerald-600"><CheckCircle2 className="h-5 w-5" /> Applied</span>
            ) : (
              <button onClick={apply} disabled={busy} data-testid="apply-now-btn" className="gjt-btn-orange rounded-xl px-8 py-3 font-bold disabled:opacity-60 flex items-center gap-2">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} {t("apply_now")}
              </button>
            )}
            <span className="text-sm text-slate-400">{job.applications || 0} applied · {job.views || 0} views</span>
          </div>
        </div>
      </div>
    </Layout>
  );
}
